import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';

const TABS = ['Club Info', 'Subcommittee', 'Gallery'];

const EMPTY_MEMBER = { name: '', role: 'Subcommittee Member', photo: '' };
const EMPTY_IMAGE  = { url: '', caption: '', wide: false };

const DEFAULT_DATA = {
  clubName: 'MNC West Cricket Club',
  subtitle: 'Uniting the Melbourne Nepalese community through the spirit of cricket',
  logo: '',
  intro: 'Following preliminary player discussions held on New Year\'s Day, the MNCW Executive Team convened to formalise the structural blueprint for the organisation\'s new sports division.',
  resolution: {
    discussion: 'Following up on the preliminary player discussions held on New Year\'s Day, the team discussed the formal structural blueprint for the sports division.',
    officialName: 'MNC West Club',
    subcommitteeTask: 'bank account setup and formal team registration',
  },
  subcommittee: [
    { name: 'Keshab Pandey',    role: 'Subcommittee Member', photo: '/assets/img/team/keshab.png' },
    { name: 'Jyoti Karki',      role: 'Subcommittee Member', photo: '/assets/img/team/jyoti.jpg'  },
    { name: 'Basudev Bhusal',   role: 'Subcommittee Member', photo: '' },
    { name: 'Prajwol Bhattari', role: 'Subcommittee Member', photo: '' },
  ],
  gallery: [
    { url: '/assets/img/banner1.jpg', caption: 'Community gathering', wide: true },
    { url: '/assets/img/banner2.jpg', caption: 'Cultural celebration',  wide: false },
  ],
};

function PhotoInput({ value, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="/assets/img/team/name.jpg or S3 URL"
        />
        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPicker(true)}>
          <i className="fas fa-images me-1"></i>Browse
        </button>
      </div>
      {value && (
        <img
          src={value}
          alt="preview"
          style={{ height: 56, width: 56, borderRadius: '50%', objectFit: 'cover', marginTop: 6, border: '2px solid #dee2e6' }}
        />
      )}
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { onChange(url); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}

function ImageInput({ value, onChange, label = 'Image URL' }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="/assets/img/banner.jpg or S3 URL"
        />
        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPicker(true)}>
          <i className="fas fa-images me-1"></i>Browse
        </button>
      </div>
      {value && (
        <img
          src={value}
          alt="preview"
          style={{ height: 80, width: 140, objectFit: 'cover', marginTop: 6, borderRadius: 6, border: '2px solid #dee2e6' }}
        />
      )}
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { onChange(url); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}

export default function CricketClubManager() {
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx]   = useState(null);
  const [formVal, setFormVal]   = useState(EMPTY_MEMBER);

  const [showImgForm, setShowImgForm] = useState(false);
  const [editImgIdx, setEditImgIdx]   = useState(null);
  const [imgFormVal, setImgFormVal]   = useState(EMPTY_IMAGE);

  useEffect(() => {
    api.get('/content/cricketclub')
      .then(r => setData({ ...DEFAULT_DATA, ...r.data }))
      .catch(() => setData({ ...DEFAULT_DATA }))
      .finally(() => setLoading(false));
  }, []);

  const persist = async (updated) => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('/content/cricketclub', updated);
      setData(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Subcommittee ──────────────────────────────────────────────────────
  const openAddMember  = () => { setEditIdx(null); setFormVal({ ...EMPTY_MEMBER }); setShowForm(true); };
  const openEditMember = (i) => { setEditIdx(i); setFormVal({ ...(data.subcommittee || [])[i] }); setShowForm(true); };

  const saveMember = async () => {
    const list = [...(data.subcommittee || [])];
    if (editIdx !== null) list[editIdx] = formVal;
    else list.push(formVal);
    await persist({ ...data, subcommittee: list });
    setShowForm(false);
  };

  const deleteMember = async (i) => {
    if (!window.confirm('Remove this member?')) return;
    await persist({ ...data, subcommittee: (data.subcommittee || []).filter((_, idx) => idx !== i) });
  };

  // ── Gallery ───────────────────────────────────────────────────────────
  const openAddImage  = () => { setEditImgIdx(null); setImgFormVal({ ...EMPTY_IMAGE }); setShowImgForm(true); };
  const openEditImage = (i) => { setEditImgIdx(i); setImgFormVal({ ...(data.gallery || [])[i] }); setShowImgForm(true); };

  const saveImage = async () => {
    const list = [...(data.gallery || [])];
    if (editImgIdx !== null) list[editImgIdx] = imgFormVal;
    else list.push(imgFormVal);
    await persist({ ...data, gallery: list });
    setShowImgForm(false);
  };

  const deleteImage = async (i) => {
    if (!window.confirm('Remove this image?')) return;
    await persist({ ...data, gallery: (data.gallery || []).filter((_, idx) => idx !== i) });
  };

  const switchTab = (i) => { setTab(i); setShowForm(false); setShowImgForm(false); };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!data)   return <div className="alert alert-danger">Could not load cricket club content.</div>;

  return (
    <div>
      <div className="mb-4">
        <h4 className="fw-bold mb-0">Cricket Club</h4>
        <p className="text-muted small mb-0">Manage the MNC West Cricket Club page content</p>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {saved  && <div className="alert alert-success py-2"><i className="fas fa-check-circle me-2"></i>Saved successfully.</div>}

      <ul className="nav nav-tabs mb-4">
        {TABS.map((t, i) => (
          <li key={t} className="nav-item">
            <button className={`nav-link ${tab === i ? 'active' : ''}`} onClick={() => switchTab(i)}>{t}</button>
          </li>
        ))}
      </ul>

      {/* ── Club Info ── */}
      {tab === 0 && (
        <form onSubmit={e => { e.preventDefault(); persist(data); }}>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Club Name</label>
                  <input className="form-control"
                    value={data.clubName || ''}
                    onChange={e => setData(d => ({ ...d, clubName: e.target.value }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Subtitle</label>
                  <input className="form-control"
                    value={data.subtitle || ''}
                    onChange={e => setData(d => ({ ...d, subtitle: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Club Logo</label>
                  <ImageInput value={data.logo} onChange={v => setData(d => ({ ...d, logo: v }))} label="Club Logo" />
                  <div className="text-muted small mt-1">Leave blank to use the default MNCW logo.</div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Intro Paragraph</label>
                  <textarea rows="3" className="form-control"
                    value={data.intro || ''}
                    onChange={e => setData(d => ({ ...d, intro: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <hr />
                  <h6 className="fw-bold mb-3">Resolution Details</h6>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Discussion Summary</label>
                  <textarea rows="3" className="form-control"
                    value={data.resolution?.discussion || ''}
                    onChange={e => setData(d => ({ ...d, resolution: { ...(d.resolution || {}), discussion: e.target.value } }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {/* ── Subcommittee ── */}
      {tab === 1 && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small">{(data.subcommittee || []).length} member{(data.subcommittee || []).length !== 1 ? 's' : ''}</span>
            <button className="btn btn-primary rounded-pill px-4" onClick={openAddMember}>
              <i className="fas fa-plus me-2"></i>Add Member
            </button>
          </div>

          {showForm && (
            <div className="card border-0 shadow-sm mb-3 border-start border-primary border-3">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">{editIdx !== null ? `Editing: ${formVal.name}` : 'New Member'}</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Name *</label>
                    <input className="form-control" value={formVal.name}
                      onChange={e => setFormVal(f => ({ ...f, name: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Role</label>
                    <input className="form-control" value={formVal.role}
                      onChange={e => setFormVal(f => ({ ...f, role: e.target.value }))}
                      placeholder="e.g. Subcommittee Member"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold">Photo</label>
                    <PhotoInput value={formVal.photo} onChange={v => setFormVal(f => ({ ...f, photo: v }))} />
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary rounded-pill px-4" onClick={saveMember}
                    disabled={saving || !formVal.name.trim()}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : 'Save Member'}
                  </button>
                  <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr><th style={{ width: 56 }}>Photo</th><th>Name</th><th>Role</th><th style={{ width: 120 }}></th></tr>
                </thead>
                <tbody>
                  {(data.subcommittee || []).length === 0 && (
                    <tr><td colSpan="4" className="text-center text-muted py-4">No members yet.</td></tr>
                  )}
                  {(data.subcommittee || []).map((m, i) => (
                    <tr key={i}>
                      <td className="align-middle">
                        {m.photo
                          ? <img src={m.photo} alt={m.name} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '1px solid #dee2e6' }} />
                          : <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="fas fa-user text-muted" style={{ fontSize: 14 }}></i>
                            </div>
                        }
                      </td>
                      <td className="align-middle fw-medium">{m.name}</td>
                      <td className="align-middle text-muted small">{m.role}</td>
                      <td className="align-middle">
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEditMember(i)}>Edit</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteMember(i)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Gallery ── */}
      {tab === 2 && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small">{(data.gallery || []).length} image{(data.gallery || []).length !== 1 ? 's' : ''}</span>
            <button className="btn btn-primary rounded-pill px-4" onClick={openAddImage}>
              <i className="fas fa-plus me-2"></i>Add Image
            </button>
          </div>

          {showImgForm && (
            <div className="card border-0 shadow-sm mb-3 border-start border-primary border-3">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">{editImgIdx !== null ? 'Edit Image' : 'New Image'}</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label small fw-bold">Image *</label>
                    <ImageInput value={imgFormVal.url} onChange={v => setImgFormVal(f => ({ ...f, url: v }))} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-bold">Caption</label>
                    <input className="form-control" value={imgFormVal.caption}
                      onChange={e => setImgFormVal(f => ({ ...f, caption: e.target.value }))}
                      placeholder="Short description"
                    />
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input className="form-check-input" type="checkbox" id="imgWide"
                        checked={!!imgFormVal.wide}
                        onChange={e => setImgFormVal(f => ({ ...f, wide: e.target.checked }))}
                      />
                      <label className="form-check-label small fw-bold" htmlFor="imgWide">Wide (spans 2 columns)</label>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary rounded-pill px-4" onClick={saveImage}
                    disabled={saving || !imgFormVal.url.trim()}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : 'Save Image'}
                  </button>
                  <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowImgForm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="row g-3">
            {(data.gallery || []).length === 0 && (
              <div className="col-12 text-center text-muted py-4">No images yet.</div>
            )}
            {(data.gallery || []).map((img, i) => (
              <div key={i} className={img.wide ? 'col-md-8' : 'col-md-4'}>
                <div className="card border-0 shadow-sm h-100">
                  {img.url && (
                    <img src={img.url} alt={img.caption}
                      style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: '8px 8px 0 0' }}
                    />
                  )}
                  <div className="card-body p-3 d-flex justify-content-between align-items-center">
                    <div>
                      <div className="small fw-medium">{img.caption || <span className="text-muted">No caption</span>}</div>
                      {img.wide && <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '0.7rem' }}>Wide</span>}
                    </div>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openEditImage(i)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => deleteImage(i)}>Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
